import type { QuoteCurationWorkspace } from "@workspace/api-client-react";
import { humanize } from "./curationUtils";

function pretty(v: unknown): string {
  if (v == null) return "None";
  if (typeof v === "string") { try { return JSON.stringify(JSON.parse(v), null, 2); } catch { return v; } }
  return JSON.stringify(v, null, 2);
}

function when(v: unknown): string {
  if (typeof v !== "string" && typeof v !== "number") return "Unknown time";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
}

type Work = { state?: string; error?: string | null; stages?: unknown } & Record<string, unknown>;

function fmt(v: unknown): string {
  if (v == null) return "None";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return JSON.stringify(v);
}

export function CurationRecordPanel({ ws, onRetry, retrying, retryError }: {
  ws: QuoteCurationWorkspace; onRetry: () => void; retrying: boolean; retryError: string | null;
}) {
  const work = (ws.work ?? null) as Work | null;
  const state = typeof work?.state === "string" ? work.state : null;
  const failed = state === "failed" || !!work?.error;
  const stages = Array.isArray(work?.stages) ? (work!.stages as unknown[]).map(fmt) : [];

  return (
    <div className="space-y-4 text-sm">
      <section aria-labelledby="cur-work-h" className="rounded-2xl border border-stone-200 bg-white p-4">
        <div className="flex items-center justify-between gap-2">
          <h4 id="cur-work-h" className="text-xs font-semibold uppercase tracking-wide text-stone-500">Derived work</h4>
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${failed ? "bg-rose-100 text-rose-700" : state === "completed" || !state ? "bg-emerald-100 text-emerald-700" : "bg-sky-100 text-sky-700"}`}>
            {state ? humanize(state) : "Idle"}
          </span>
        </div>
        {stages.length > 0 && <p className="mt-2 text-xs text-stone-500">Stages: {stages.map(humanize).join(", ")}</p>}
        {work?.error && <p role="alert" className="mt-2 rounded-xl bg-rose-50 px-3 py-2 text-xs text-rose-800">{fmt(work.error)}</p>}
        {state && state !== "completed" && !failed && <p className="mt-2 text-xs text-stone-400">Evidence and vectors may be stale until this finishes.</p>}
        {failed && (
          <button type="button" onClick={onRetry} disabled={retrying}
            className="mt-3 rounded-xl border border-stone-200 px-3 py-1.5 text-xs font-medium text-stone-700 transition hover:bg-stone-100 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400">
            {retrying ? "Retrying" : "Retry failed work"}
          </button>
        )}
        {retryError && <p role="alert" className="mt-2 text-xs text-red-700">{retryError}</p>}
      </section>

      {ws.overrides.length > 0 && (
        <section className="rounded-2xl border border-stone-200 bg-white p-4">
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-stone-500">Human overrides in force</h4>
          <div className="flex flex-wrap gap-1.5">{ws.overrides.map((o) => <span key={o} className="rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-800">{humanize(o)}</span>)}</div>
        </section>
      )}

      <section className="rounded-2xl border border-stone-200 bg-stone-50 p-4">
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-stone-500">Model facts (read-only)</h4>
        {ws.facts.length === 0 ? <p className="text-xs text-stone-400">No model facts recorded.</p> : (
          <ul className="space-y-3">
            {ws.facts.map((f, i) => (
              <li key={i} className="rounded-xl border border-stone-200 bg-white p-2.5 text-xs">
                <dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5">
                  <dt className="text-stone-400">Outcome</dt><dd className="break-words text-stone-700">{fmt(f.outcome)}</dd>
                  <dt className="text-stone-400">Classifier</dt><dd className="break-words text-stone-700">{fmt(f.classifier_version)}</dd>
                  <dt className="text-stone-400">Model</dt><dd className="break-words text-stone-700">{fmt(f.model_used)}</dd>
                  <dt className="text-stone-400">Processed</dt><dd className="text-stone-700">{when(f.processed_at)}</dd>
                  <dt className="text-stone-400">Input</dt><dd className="break-all font-mono text-[10px] text-stone-500">{fmt(f.input_fingerprint)}</dd>
                </dl>
                <details className="mt-1.5">
                  <summary className="cursor-pointer text-stone-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 rounded">Raw result</summary>
                  <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-stone-50 p-2 font-mono text-[10px] text-stone-600">{pretty(f.raw_result)}</pre>
                </details>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-2xl border border-stone-200 bg-white p-4">
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-stone-500">Activity on this quote</h4>
        <p className="text-xs text-stone-600">{Object.entries(ws.counts).map(([k, v]) => `${humanize(k)} ${v}`).join(" · ") || "No activity."}</p>
      </section>

      <section className="rounded-2xl border border-stone-200 bg-white p-4">
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-stone-500">History</h4>
        {ws.history.length === 0 ? <p className="text-xs text-stone-400">No curation history yet.</p> : (
          <ol className="space-y-3 border-l border-stone-200 pl-3">
            {ws.history.map((h, i) => (
              <li key={i} className="text-xs">
                <p className="font-medium text-stone-700">{when(h.created_at)}</p>
                <div className="mt-1 grid gap-1.5 sm:grid-cols-2 md:grid-cols-1">
                  <div><p className="text-[10px] uppercase tracking-wide text-stone-400">Before</p>
                    <pre className="max-h-32 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-rose-50/60 p-2 font-mono text-[10px] text-stone-600">{pretty(h.before_data)}</pre></div>
                  <div><p className="text-[10px] uppercase tracking-wide text-stone-400">After</p>
                    <pre className="max-h-32 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-emerald-50/60 p-2 font-mono text-[10px] text-stone-600">{pretty(h.after_data)}</pre></div>
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}
